import {
	Combobox as HeadlessUICombobox,
	ComboboxInput as HeadlessUIComboboxInput,
	ComboboxOption as HeadlessUIComboboxOption,
	ComboboxOptions as HeadlessUIComboboxOptions,
} from "@headlessui/react";
import { filter } from "@msuite/utils/array";
import clsx from "clsx";
import React, { useRef, useState } from "react";
import { type Control, Controller, type FieldValues, type Path } from "react-hook-form";
import { useDebounced } from "../hooks";
import { Icon } from "../icons";
import { ComboboxButton } from "./combobox-button";
import { type ExtendedInputWrapperProps, InputWrapper } from "./input-wrapper";
import { RemoveWrapper } from "./remove-wrapper";

/** Props Interface */
interface ComboboxProps<T extends FieldValues, U> extends ExtendedInputWrapperProps<T> {
	options: Array<U> | undefined;
	control: Control<T>;
	name: Path<T>;
	displayValue: (item: U) => string;
	keyExtractor: (item: U) => string;
	renderOption?: (item: U) => React.JSX.Element;
	buttons?: Array<{
		tags: Array<string>;
		render: (item: U) => React.JSX.Element;
		onClick: (item: U) => void;
	}>;
	placeholder: string;
	isNullable?: boolean;
	immediate?: boolean;
	onRemove?: () => void;
	onSelect?: (item: U) => void;
	middleware?: (item: U) => void;
	onKeyDown?: (event: React.KeyboardEvent) => void;
	"data-cy"?: string;
	inputRef?: React.Ref<HTMLInputElement>;
	defaultValue?: string;
}

function Combobox<T extends FieldValues, U>({
	options = [],
	control,
	name,
	displayValue,
	keyExtractor,
	placeholder,
	renderOption,
	autoFocus,
	immediate = true,
	isDisabled,
	isNullable = true,
	onRemove,
	onSelect,
	buttons = [],
	middleware,
	onKeyDown,
	inputRef,
	"data-cy": dataCy,
	defaultValue,
	...props
}: ComboboxProps<T, U>) {
	/** State */
	const [query, setQuery] = useState<string>("");
	const { debouncedValue } = useDebounced({ value: query, timeout: 0 });
	const ref = useRef<HTMLDivElement>(null) as React.Ref<HTMLDivElement>;

	/** Computed */
	const filtered = filter(
		[
			...(buttons || []).map((button) => ({
				type: "button",
				...button,
			})),
			...options,
		],
		debouncedValue,
	);

	/** Render */
	return (
		<Controller
			control={control}
			name={name}
			defaultValue={(() => {
				try {
					return keyExtractor(defaultValue as U) as any;
				} catch {
					return undefined;
				}
			})()}
			render={({ field, formState }) => (
				<InputWrapper
					{...props}
					name={name}
					errors={formState.errors}
				>
					<RemoveWrapper
						hasValue={!!field.value}
						isNullable={isNullable}
						handleOnRemove={() => {
							onRemove?.();
							field.onChange(null);
						}}
					>
						<HeadlessUICombobox
							immediate={immediate}
							defaultValue={field.value}
							value={field.value}
							onChange={(item) => {
								try {
									middleware?.(item as U);
									if ("onClick" in (item as object)) {
										(item as any).onClick();
									} else {
										if (onSelect) {
											onSelect?.(
												options.find((option) => {
													try {
														return keyExtractor(option) === keyExtractor(item as any);
													} catch {
														return false;
													}
												}) as U,
											);
										} else {
											field.onChange(keyExtractor(item as any));
										}
									}
								} catch (error) {
									console.error(error);
								}
							}}
							disabled={isDisabled}
							virtual={{
								options: filtered as any,
							}}
						>
							<HeadlessUIComboboxInput
								ref={inputRef as any}
								id={field.name}
								className="z-10 input"
								aria-label="combobox"
								autoComplete="off"
								displayValue={(item) => {
									try {
										const selectedOption = options.find((option) => keyExtractor(option) === item);
										if (!selectedOption) return query;
										return displayValue(selectedOption);
									} catch {
										return query;
									}
								}}
								onChange={(event) => setQuery(event.target.value)}
								placeholder={placeholder}
								autoFocus={autoFocus}
								onKeyDown={onKeyDown}
								data-cy={dataCy || "combobox-input"}
							/>
							<HeadlessUIComboboxOptions
								className="empty:invisible w-[var(--input-width)] listbox-options !max-h-[250px]"
								transition={false}
								anchor={{
									to: "bottom",
									gap: 5,
								}}
								ref={ref as any}
							>
								{(virtualItem) =>
									virtualItem.option.type === "button" ? (
										<HeadlessUIComboboxOption
											value={virtualItem.option}
											className={clsx("group w-full listbox-option")}
										>
											{virtualItem.option.render()}
										</HeadlessUIComboboxOption>
									) : (
										<HeadlessUIComboboxOption
											value={virtualItem.option}
											className={clsx(
												"group w-full flex flex-row listbox-option",
												`listbox-option-${dataCy}`,
											)}
										>
											{renderOption ? (
												renderOption(virtualItem.option)
											) : (
												<p>{displayValue(virtualItem.option)}</p>
											)}
											<div className="listbox-option-icon">
												<Icon name="Check" />
											</div>
										</HeadlessUIComboboxOption>
									)
								}
							</HeadlessUIComboboxOptions>
						</HeadlessUICombobox>
					</RemoveWrapper>
				</InputWrapper>
			)}
		/>
	);
}

Combobox.Button = ComboboxButton;

export { Combobox };
